import { ClientError } from 'graphql-request';

import { ProductType } from 'src/common/interfaces/Tenant';
import { gqlClient } from 'src/common/services/api/GraphQLRequestApi';
import { Logger } from 'src/common/services/Logger';

import { buildMobileScansListFallbackVariables } from './helpers/buildFallbackVariables';
import { buildMobileScansListVariables } from './helpers/buildVariables';
import { transformMobileScansListFallbackResults } from './helpers/transformFallbackResults';
import { transformMobileScansListResults } from './helpers/transformResults';
import { MOBILE_SCANS_LIST_ENTITY_NAME } from './MobileScansList.constants';
import { MFA_SCANS_LIST_QUERY, MSDK_SCANS_LIST_QUERY } from './MobileScansList.query';
import {
	IGetMobileScansListParams,
	IMobileScansListFallbackQuery,
	IMobileScansListQuery,
	IMobileScansListResult,
	MobileScansProductType
} from './MobileScansList.types';
import { MOBILE_SCANS_LIST_FALLBACK_QUERY } from './MobileScansListFallback.query';

const SCHEMA_UNAVAILABLE_ERROR_MARKERS = [
	'Cannot query field',
	'Unknown type',
	'Unknown argument',
	'does not exist',
	'not found'
];

const getErrorMessages = (error: unknown): string[] => {
	if (error instanceof ClientError) {
		const responseErrors =
			error.response.errors?.map((responseError) => responseError.message) ?? [];
		return [error.message, ...responseErrors];
	}

	if (error instanceof Error) {
		return [error.message];
	}

	return [];
};

const isMobileScansListSchemaUnavailableError = (error: unknown): boolean =>
	getErrorMessages(error).some(
		(message) =>
			message.includes(MOBILE_SCANS_LIST_ENTITY_NAME) &&
			SCHEMA_UNAVAILABLE_ERROR_MARKERS.some((marker) => message.includes(marker))
	);

const getMobileScansListFallback = async (
	params: IGetMobileScansListParams
): Promise<IMobileScansListResult> => {
	try {
		const variables = buildMobileScansListFallbackVariables(params);
		const result = await gqlClient.request<IMobileScansListFallbackQuery>(
			MOBILE_SCANS_LIST_FALLBACK_QUERY,
			variables as unknown as Record<string, unknown>
		);

		return transformMobileScansListFallbackResults(result, params);
	} catch (error) {
		Logger.error(
			'MobileScansListService -> getMobileScansListFallback failed with an error:',
			error
		);
		throw error;
	}
};

const getQuery = (productName?: MobileScansProductType): string =>
	productName === ProductType.MOBILE_SDK ? MSDK_SCANS_LIST_QUERY : MFA_SCANS_LIST_QUERY;

export const getMobileScansList = async (
	params: IGetMobileScansListParams
): Promise<IMobileScansListResult> => {
	try {
		const variables = buildMobileScansListVariables(params);
		const result = await gqlClient.request<IMobileScansListQuery>(
			getQuery(params.productName),
			variables
		);

		return transformMobileScansListResults(result, params.productName);
	} catch (error) {
		if (isMobileScansListSchemaUnavailableError(error)) {
			Logger.warn(
				'MobileScansListService -> mobile_scans_list is unavailable, using temporary fallback:',
				error
			);
			return getMobileScansListFallback(params);
		}

		Logger.error('MobileScansListService -> getMobileScansList failed with an error:', error);
		throw error;
	}
};
